import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Activity, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

export const TechnicalAudit = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [siteUrl, setSiteUrl] = useState('');
  const [auditResults, setAuditResults] = useState('');

  const handleAudit = async () => {
    if (!siteUrl) {
      toast({
        title: 'Missing Information',
        description: 'Please provide a site URL',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('seo-automation', {
        body: {
          action: 'technical_audit',
          data: { siteUrl }
        }
      });

      if (error) throw error;

      setAuditResults(data.audit_results);
      toast({
        title: 'Audit Complete',
        description: 'Technical SEO audit completed',
      });
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: 'Error',
        description: 'Failed to perform technical audit',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Technical SEO Automation
          </CardTitle>
          <CardDescription>
            Audit website for technical issues and get actionable recommendations
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="siteUrl">Website URL</Label>
            <Input
              id="siteUrl"
              placeholder="https://example.com"
              value={siteUrl}
              onChange={(e) => setSiteUrl(e.target.value)}
            />
          </div>

          <Button onClick={handleAudit} disabled={loading} className="w-full">
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Running Technical Audit...
              </>
            ) : (
              <>
                <Activity className="w-4 h-4 mr-2" />
                Run Technical Audit
              </>
            )}
          </Button>

          {auditResults && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-2">
                  <p className="font-semibold">Audit Results</p>
                  <div className="text-sm whitespace-pre-wrap">
                    {auditResults}
                  </div>
                </div>
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Page Speed</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">Run audit</div>
                <p className="text-xs text-muted-foreground">to see score</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Mobile Friendly</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">Run audit</div>
                <p className="text-xs text-muted-foreground">to see score</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Broken Links</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">Run audit</div>
                <p className="text-xs text-muted-foreground">to see count</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Meta Tags</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">Run audit</div>
                <p className="text-xs text-muted-foreground">to see status</p>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};